import { FC, useEffect } from 'react';
import { useAuthForgotPasswordRequest } from './queries/authForgotPassword.generated';
import { useParams } from 'react-router-dom';
import { Button, Form, Input } from 'antd';
//eslint-disable-next-line no-restricted-imports
import { Link } from 'react-router-dom';
import { emailValidator } from '@/libs/validators';
import { gettext, useGettext } from '@cranium/i18n';
import type { IdentityTypeEnum } from '@/gql/types.generated';
import { Title } from '@pages/Login/component';
import styled from 'styled-components';
import { Step } from './types';

function requiredValidator(type: 'email' | 'phone') {
  return (rule: any, value: string, callBack: (message?: any) => void) => {
    callBack(!value ? (type === 'email' ? gettext('Email is incorrect') : gettext('Phone is incorrect')) : undefined);
  };
}

type CurrentEmailProps = {
  setStep: (data: Step) => void;
  initialValues?: string;
};

export const CurrentEmail: FC<CurrentEmailProps> = ({ setStep, initialValues }) => {
  const { gettext } = useGettext();
  const { identity } = useParams<{ identity: IdentityTypeEnum }>();
  const recoverPassword = useAuthForgotPasswordRequest();
  const [form] = Form.useForm<{ email: string }>();
  useEffect(() => {
    if (initialValues) {
      form.setFieldValue('email', initialValues);
    }
  }, []);

  const handleSubmit = () => {
    form.validateFields().then(values => {
      return recoverPassword({
        email: values.email,
        type: identity
      }).then(resp => {
        if (resp?.data?.authForgotPassword?.success) {
          setStep({ step: 1, email: values.email });
        }
      });
    });
  };

  return (
    <Main>
      <Title>{gettext('Forgot your password?')}</Title>
      <Description>{gettext('Please enter your email to reset your password')}</Description>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label={gettext('Email')}
          name="email"
          rules={[{ validator: emailValidator }, { validator: requiredValidator('email') }]}
        >
          <Input type="email" placeholder={gettext('Email')} />
        </Form.Item>
        <Link to={`/login/${identity}`}>
          <i className="icon-arrow-left" />
          {gettext('Back to login page')}
        </Link>
        <Button type="primary" onClick={() => handleSubmit()}>
          {gettext('Next')}
        </Button>
      </Form>
    </Main>
  );
};

const Main = styled.div`
  width: 420px;
  max-width: 100%;
  > h1 {
    margin-bottom: 0;
  }
  > form {
    display: flex;
    flex-direction: column;
  }
  button {
    align-self: flex-end;
  }
  a {
    display: flex;
    align-items: center;
    gap: 4px;
    &:hover {
      color: var(--green-7);
      text-decoration: none;
    }
  }
`;

const Description = styled.p`
  font: var(--font-regular-14);
  color: var(--gray-7);
`;
